import type { ReactNode } from 'react';
import { makeStyles, tokens, mergeClasses, Body1, Caption1 } from '@fluentui/react-components';
import { SearchInfo24Regular } from '@fluentui/react-icons';

/**
 * Shared empty-state primitive (spec §4.6). Replaces every bespoke
 * "No data" / "No Calls Found" idiom across the app: icon, one-line title,
 * optional one-line hint, optional action.
 *
 * `compact` sizes it down for a small chart-adjacent panel instead of a
 * full-page/full-card empty area.
 */
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    textAlign: 'center',
    width: '100%',
  },
  compact: {
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalM}`,
    gap: tokens.spacingVerticalS,
  },
  iconWrap: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '360px',
  },
});

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  /** Sizes the empty state down for a chart-adjacent panel rather than a full page/card. */
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
  compact,
  className,
  ...rest
}: EmptyStateProps) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.root, compact && styles.compact, className)} {...rest}>
      <div className={styles.iconWrap}>{icon ?? <SearchInfo24Regular />}</div>
      <Body1 className={styles.title}>{title}</Body1>
      {hint && <Caption1 className={styles.hint}>{hint}</Caption1>}
      {action}
    </div>
  );
}
